import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';
import { sanitizeSchema, extractEmojiFromUnicode } from '../utils/sanitizeLabel';

// Leading emoji (incl. variation selectors / keycaps / flag letters) — used to
// detect a real emoji and to strip a duplicated leading emoji off a label.
const LEADING_EMOJI_RE = /^[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{20E3}\u{1F1E6}-\u{1F1FF}]+/u;

// Generic, market-agnostic categories shown in the "My market is not listed" flow
const GENERIC_CATEGORY_SUGGESTIONS = [
  'Fashion & Clothing',
  'Food & Beverages',
  'Home & Kitchen',
  'Beauty & Personal Care',
  'Electronics & Gadgets',
  'Phones & Tablets',
  'Laptops & Computers',
  'Repairs & Services',
  'Books & Stationery',
  'Baby & Kids',
  'Sports & Outdoors',
  'Health & Wellness',
  'Automotive & Spare Parts',
  'Building Materials',
  'Agriculture & Farm Produce',
  'Bags & Shoes',
  'Jewelry & Accessories',
  'Art, Crafts & Decor'
];

// Quick-add sub-categories that fit most sellers
const GENERIC_SUB_CATEGORY_SUGGESTIONS = [
  'Brand new',
  'Fairly used',
  'Wholesale',
  'Retail',
  'Made to order'
];

// Optimized helper to extract emoji from category data
const extractCategoryEmoji = (icon) => {
  if (!icon) return null;

  // URL images pass through so the UI can render them with <img>
  if (icon.startsWith('http')) return icon;

  // Already a real emoji (e.g. 🔧 or 🖨️) — return it as is
  const emoji = icon.match(LEADING_EMOJI_RE);
  if (emoji && emoji[0]) return emoji[0];

  // Mangled escape forms (e.g. "{u1F527}") — convert to a real emoji
  const converted = extractEmojiFromUnicode(icon);
  if (converted) return converted;

  // Fallback: try to parse a hex code
  const hexMatch = icon.match(/(?:0x|\\u|\{u|U\+)([0-9A-Fa-f]{4,6})/);
  if (hexMatch) {
    try {
      const codePoint = parseInt(hexMatch[1], 16);
      return String.fromCodePoint(codePoint);
    } catch (e) {
      return null;
    }
  }

  return null;
};

// Helper to clean and format category label with emoji
const formatCategoryDisplay = (category) => {
  if (!category) return { label: '', emoji: null };

  let emoji = null;
  let cleanLabel = category.label || '';

  // Try to get emoji from icon field
  if (category.icon) {
    emoji = extractCategoryEmoji(category.icon);
  }

  // If no emoji from icon, pull the leading emoji off the label itself
  if (!emoji) {
    const lead = cleanLabel.match(LEADING_EMOJI_RE);
    if (lead && lead[0]) emoji = lead[0];
  }

  // If the label starts with the same emoji (it was converted from a
  // "{u1F527} Label" prefix), drop it so the chip doesn't show it twice.
  if (emoji) {
    cleanLabel = cleanLabel.replace(emoji, '').replace(/^\s+/, '');
  }

  // Final safety net: remove any still-mangled prefix
  cleanLabel = cleanLabel
    .replace(/^\{u[0-9A-Fa-f]+\}\s*/, '')
    .replace(/^\\u\{[0-9A-Fa-f]+\}\s*/, '')
    .trim();

  return { label: cleanLabel, emoji };
};

const SelectMarketPage = () => {
  const navigate = useNavigate();

  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(true);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // "My market is not listed" (generic) flow state
  const [genericMode, setGenericMode] = useState(false);
  const [genericCategory, setGenericCategory] = useState('');
  const [genericCategoryInput, setGenericCategoryInput] = useState('');
  const [genericSubCategories, setGenericSubCategories] = useState([]);
  const [genericSubInput, setGenericSubInput] = useState('');
  const [genericMarket, setGenericMarket] = useState('');

  // Memoize market icons to avoid re-renders
  const getMarketIcon = useCallback((market) => {
    if (!market.icon) return '🏪';
    if (market.icon.startsWith('http')) {
      return market.icon;
    }
    // Try to extract emoji from market icon
    const emoji = extractCategoryEmoji(market.icon);
    return emoji || '🏪';
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadMarkets = async () => {
      try {
        const response = await api.get(API_CONFIG.MARKETS.GET_MARKETS);
        const data = response.data?.data || [];
        if (mounted) {
          setMarkets(data.filter((m) => m.is_active));
        }
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.error?.message || 'Failed to load markets. Please try again.');
        }
      } finally {
        if (mounted) setLoadingMarkets(false);
      }
    };
    loadMarkets();
    return () => { mounted = false; };
  }, []);

  const loadCategories = async (market) => {
    setLoadingSchema(true);
    setCategories([]);
    setSelectedCategories([]);
    try {
      const response = await api.get(`${API_CONFIG.MARKETS.MARKET_FORM_SCHEMA}/${market.slug}/form-schema`);
      const schema = sanitizeSchema(response.data?.data?.schema);
      const menu = Array.isArray(schema?.menu) ? schema.menu : [];

      // Process categories with emoji extraction
      const processedCategories = menu
        .filter((item) => item?.label)
        .map((item) => {
          // Get display label and emoji
          const { label: cleanLabel, emoji } = formatCategoryDisplay(item);

          return {
            id: item.id,
            label: cleanLabel,
            originalLabel: item.label, // Keep original for selection
            icon: emoji || item.icon,
            description: item.description || null,
            hasEmoji: !!emoji
          };
        });

      setCategories(processedCategories);
    } catch (err) {
      if (!err.response || err.response?.status !== 404) {
        setError('Failed to load market categories.');
      }
    } finally {
      setLoadingSchema(false);
    }
  };

  const handleSelectMarket = (market) => {
    setSelectedMarket(market);
    setError(null);
    loadCategories(market);
  };

  const toggleCategory = (label) => {
    setSelectedCategories((prev) =>
      prev.includes(label) ? prev.filter((c) => c !== label) : [...prev, label]
    );
  };

  const saveMarket = async (payload) => {
    setSaving(true);
    try {
      await api.post(API_CONFIG.MARKETS.UPDATE_MARKET, payload);
      return true;
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to save your selection. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveGeneric = async (payload) => {
    setSaving(true);
    try {
      await api.post(API_CONFIG.MARKETS.SAVE_GENERIC_CATEGORY, payload);
      return true;
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to save your category. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedMarket) return;
    const ok = await saveMarket({
      market_id: selectedMarket.id,
      categories: selectedCategories,
    });
    if (ok) navigate('/wallet');
  };

  const handleGenericContinue = async () => {
    if (!genericCategory.trim()) {
      setError('Please select or enter a category before continuing.');
      return;
    }
    const ok = await saveGeneric({
      category: genericCategory.trim(),
      sub_categories: genericSubCategories,
      market_name: genericMarket.trim() || null,
    });
    if (ok) navigate('/wallet');
  };

  const handleSkip = async () => {
    const ok = await saveMarket({ skipped: true });
    if (ok) navigate('/setup/upload-product');
  };

  const addGenericSubCategory = () => {
    const value = genericSubInput.trim();
    if (!value) return;
    setGenericSubCategories((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setGenericSubInput('');
  };

  const removeGenericSubCategory = (value) => {
    setGenericSubCategories((prev) => prev.filter((s) => s !== value));
  };

  // Memoize market list to prevent unnecessary re-renders
  const marketList = useMemo(() => {
    if (!selectedMarket) {
      return markets.map((market) => (
        <button
          key={market.id}
          onClick={() => handleSelectMarket(market)}
          className="group text-left p-4 rounded-xl border-2 border-gray-100 bg-gray-50/60 hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-2xl overflow-hidden shrink-0">
              {getMarketIcon(market).startsWith('http') ? (
                <img
                  src={getMarketIcon(market)}
                  alt={market.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{getMarketIcon(market)}</span>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm text-gray-900 group-hover:text-blue-700">
                {market.name}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {market.member_count > 0
                  ? `${market.member_count.toLocaleString()} sellers`
                  : 'Growing community'}
              </p>
            </div>
          </div>
          {market.description && (
            <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-2">
              {market.description}
            </p>
          )}
        </button>
      ));
    }
    return null;
  }, [markets, selectedMarket, getMarketIcon]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 lg:py-8 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white lg:rounded-2xl overflow-hidden shadow-sm">
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 sm:px-10 sm:py-10">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white">
                    Tell us where you sell
                  </h1>
                  <p className="text-sm text-blue-100/90 mt-1">
                    Choose a market to showcase your business to the right buyers.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-white" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/40" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/40" />
              </div>
            </div>
          </div>

          <div className="px-6 py-6 sm:px-10 sm:py-8">
            {error && (
              <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {loadingMarkets ? (
              <div className="flex flex-col items-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-500 text-sm">Loading markets...</p>
              </div>
            ) : genericMode ? (
              <div>
                <button
                  onClick={() => setGenericMode(false)}
                  className="text-xs text-blue-600 hover:text-blue-800 mb-5 underline inline-flex items-center gap-1"
                >
                  ← Back to markets
                </button>

                {/* Step 1: category */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">What do you sell?</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Select a category or type your own.
                  </p>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {GENERIC_CATEGORY_SUGGESTIONS.map((cat) => {
                      const active = genericCategory === cat;
                      return (
                        <button
                          key={cat}
                          onClick={() => setGenericCategory(cat)}
                          className={`px-4 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                            active
                              ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-700'
                          }`}
                        >
                          {cat}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <input
                      value={genericCategoryInput}
                      onChange={(e) => setGenericCategoryInput(e.target.value)}
                      placeholder="Or type your own category (e.g. Second-hand shoes)"
                      className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={() => {
                        const value = genericCategoryInput.trim();
                        if (value) setGenericCategory(value);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 px-5 rounded-xl transition-all duration-200"
                    >
                      Add
                    </button>
                  </div>

                  {genericCategory && (
                    <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium">
                      <span>{genericCategory}</span>
                      <button
                        onClick={() => setGenericCategory('')}
                        className="text-white/80 hover:text-white text-base leading-none"
                        aria-label="Remove category"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>

                {/* Step 2: sub-categories */}
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Sub-categories <span className="font-normal text-gray-400">(optional)</span>
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Add sub-categories so buyers can find you faster.
                  </p>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {GENERIC_SUB_CATEGORY_SUGGESTIONS.map((sub) => {
                      const active = genericSubCategories.includes(sub);
                      return (
                        <button
                          key={sub}
                          onClick={() =>
                            setGenericSubCategories((prev) =>
                              prev.includes(sub) ? prev.filter((s) => s !== sub) : [...prev, sub]
                            )
                          }
                          className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                            active
                              ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-700'
                          }`}
                        >
                          {sub}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <input
                      value={genericSubInput}
                      onChange={(e) => setGenericSubInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addGenericSubCategory();
                        }
                      }}
                      placeholder="Or type a sub-category (e.g. Phone accessories)"
                      className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={addGenericSubCategory}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 px-5 rounded-xl transition-all duration-200"
                    >
                      Add
                    </button>
                  </div>

                  {genericSubCategories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {genericSubCategories.map((sub) => (
                        <span
                          key={sub}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border border-gray-200 text-sm font-medium text-gray-700"
                        >
                          {sub}
                          <button
                            onClick={() => removeGenericSubCategory(sub)}
                            className="text-gray-400 hover:text-red-500 text-base leading-none"
                            aria-label={`Remove ${sub}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Optional market field */}
                <div className="mt-8">
                  <label className="block text-sm font-semibold text-gray-900">
                    Market / location{' '}
                    <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Tell buyers where you sell, e.g. a local market or street.
                  </p>
                  <input
                    value={genericMarket}
                    onChange={(e) => setGenericMarket(e.target.value)}
                    placeholder="e.g. Computer Village, Lagos"
                    className="mt-3 w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            ) : markets.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-4xl mb-3">🏪</div>
                <h3 className="text-lg font-semibold text-gray-900">No markets available yet</h3>
                <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                  Markets are coming soon. You can tell us what you sell instead or set it up later from your dashboard.
                </p>
                <button
                  onClick={() => { setGenericMode(true); setError(null); }}
                  className="mt-5 border-2 border-dashed border-blue-300 text-blue-700 hover:border-blue-500 hover:bg-blue-50 font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm"
                >
                  🏷️ My market is not listed — tell us what you sell instead
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                  {selectedMarket ? 'Switch market' : 'Choose a market'}
                </h2>

                {!selectedMarket && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                    {marketList}
                  </div>
                )}

                {!selectedMarket && (
                  <div className="mb-8">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 border-t border-gray-200" />
                      <span className="text-xs text-gray-400 uppercase tracking-wide">or</span>
                      <div className="flex-1 border-t border-gray-200" />
                    </div>
                    <button
                      onClick={() => { setGenericMode(true); setError(null); }}
                      className="mt-4 w-full sm:w-auto border-2 border-dashed border-blue-300 text-blue-700 hover:border-blue-500 hover:bg-blue-50 font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm"
                    >
                      🏷️ My market is not listed — tell us what you sell instead
                    </button>
                  </div>
                )}

                {selectedMarket && (
                  <div className="mb-8">
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                      <div className="w-10 h-10 rounded-lg bg-white border border-blue-200 flex items-center justify-center text-xl overflow-hidden shrink-0">
                        {getMarketIcon(selectedMarket).startsWith('http') ? (
                          <img
                            src={getMarketIcon(selectedMarket)}
                            alt={selectedMarket.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span>{getMarketIcon(selectedMarket)}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm text-gray-900">{selectedMarket.name}</h3>
                        <button
                          onClick={() => { setSelectedMarket(null); setCategories([]); setSelectedCategories([]); }}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-0.5 underline"
                        >
                          Change market
                        </button>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">
                          What do you sell? <span className="font-normal text-gray-400">(optional)</span>
                        </h3>
                        {loadingSchema && (
                          <span className="text-xs text-gray-400 inline-flex items-center gap-1.5">
                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></span>
                            Loading categories...
                          </span>
                        )}
                      </div>

                      {!loadingSchema && categories.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {categories.map((category) => {
                            const active = selectedCategories.includes(category.label);
                            return (
                              <button
                                key={category.id}
                                onClick={() => toggleCategory(category.label)}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                                  active
                                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-700'
                                }`}
                              >
                                {category.icon && (
                                  <span className={active ? '' : 'text-base'}>
                                    {category.icon}
                                  </span>
                                )}
                                <span>{category.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : !loadingSchema ? (
                        <p className="text-sm text-gray-500">
                          No categories available for this market yet. You can continue without selecting one.
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-gray-100">
              <button
                onClick={handleSkip}
                disabled={saving}
                className="w-full sm:w-auto border-2 border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 font-semibold py-3 px-8 rounded-xl transition-all duration-200 hover:bg-gray-50 disabled:opacity-50"
              >
                Skip for now
              </button>

              <button
                onClick={genericMode ? handleGenericContinue : handleContinue}
                disabled={saving || (genericMode ? !genericCategory.trim() : !selectedMarket)}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {saving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectMarketPage;
